import { useState, MouseEvent, Suspense } from "react";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import {
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import {
  Cached as CachedIcon,
  Error as ErrorIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  MoreVert as MoreVertIcon,
} from "@mui/icons-material";
import { useConfirm } from "material-ui-confirm";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";

import { useLEZs, usePermissions, useScenarioDeleteMutation } from "src/hooks";
import { isDisabled } from "src/utils/permission.utils";
import { Case, Scenario, CopyStatus } from "src/types";
import { ErrorsDialog } from "src/components/ErrorsDialog";
import { ListItemAsButton } from "./ListItemAsButton";
import { ScenarioCopyDialog } from "./dialogs/ScenarioCopyDialog";
import { IsEnabled, IsVisible } from "./permissions";
import { ErrorBoundary } from "react-error-boundary";

export function ScenarioListItem(props: { case: Case; scenario: Scenario }) {
  const { case: acase, scenario } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const confirm = useConfirm();

  const { caseId = "", scenarioId = "" } = useParams<{ caseId: string; scenarioId: string }>();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isOpen, setIsOpen] = useState(scenario.id.toString() === scenarioId);
  const [isCopyDialogOpen, setIsCopyDialogOpen] = useState(false);
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);

  const deleteMutation = useScenarioDeleteMutation(scenario.id);

  /**
   * Functions
   */
  function handleCopy(event: MouseEvent) {
    event.stopPropagation();
    event.preventDefault();
    setIsCopyDialogOpen(true);
  }

  function handleDelete(event: MouseEvent) {
    event.stopPropagation();
    event.preventDefault();

    confirm({
      confirmationButtonProps: { autoFocus: true },
      title: t("label.areYouSure"),
      description: t("confirm.deleteScenario", { name: scenario.name }),
    })
      .then(() => deleteMutation.mutate())
      .then(() => navigate(`/cases/${caseId}`));
  }

  function handleOpenMenu(event: React.MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  }

  function handleCloseMenu(event: React.MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(null);
  }

  return render();

  function render() {
    return (
      <Styles>
        <ListItemAsButton
          key={scenario.id}
          className="scenario-list-header"
          onClick={() => setIsOpen(!isOpen)}
          button={!scenario.copyStatus || scenario.copyStatus === CopyStatus.FINISHED}
        >
          <Status scenario={scenario} open={isOpen} onShowStatus={() => setIsStatusDialogOpen(true)} />
          <ListItemText className="scenario-name" primary={scenario.name} />

          <IconButton
            onMouseDown={(event) => event.stopPropagation()}
            onMouseUp={(event) => event.stopPropagation()}
            onClick={handleOpenMenu}
            size="large"
          >
            <MoreVertIcon />
          </IconButton>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu}>
            <IsVisible permission={acase.permissions.copyScenario}>
              <CopyMenuItem acase={acase} scenario={scenario} onClick={handleCopy} />
            </IsVisible>
            <IsVisible permission={scenario.permissions.delete}>
              <DeleteMenuItem scenario={scenario} onClick={handleDelete} />
            </IsVisible>
          </Menu>
        </ListItemAsButton>
        <ErrorBoundary fallback={null}>
          <Suspense
            fallback={
              <Collapse in={isOpen} timeout="auto" unmountOnExit>
                <List>
                  <ListItem sx={{ paddingLeft: "80px" }}>
                    <ListItemText primary={t("label.loading")} />
                  </ListItem>
                </List>
              </Collapse>
            }
          >
            <ListItemCollapse acase={acase} scenario={scenario} open={isOpen} />
          </Suspense>
        </ErrorBoundary>

        <ScenarioCopyDialog
          open={isCopyDialogOpen}
          onClose={() => setIsCopyDialogOpen(false)}
          caseId={caseId}
          scenarioId={scenario.id}
        />
        <ErrorsDialog
          data={scenario.copyErrors}
          open={isStatusDialogOpen}
          onClose={() => setIsStatusDialogOpen(false)}
        />
      </Styles>
    );
  }
}

function ListItemCollapse(props: { acase: Case; scenario: Scenario; open: boolean }) {
  const { acase, scenario, open } = props;
  const { t } = useTranslation();

  const { data: permissions } = usePermissions();
  const { data: lezsData } = useLEZs(scenario.id);
  const firstLez = lezsData?.zones?.[0];

  return render();

  function render() {
    if (!permissions) return null;
    if (scenario.copyStatus === CopyStatus.FAILED) return null;
    if (scenario.copyStatus === CopyStatus.COPYING) return null;

    return (
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List component="div" className="scenario-list-items">
          <ListItemButton end className="nested" component={NavLink} to={`/cases/${acase.id}/scenarios/${scenario.id}`}>
            <ListItemText primary={t("label.general")} />
          </ListItemButton>

          <ListItemButton
            className="nested"
            component={NavLink}
            to={`/cases/${acase.id}/scenarios/${scenario.id}/emissions`}
          >
            <ListItemText primary={t("label.emissions")} />
          </ListItemButton>

          <ListItemButton className="nested" disabled>
            <ListItemText primary={t("label.backgroundConcentrations")} />
          </ListItemButton>

          <ListItemButton className="nested" disabled>
            <ListItemText primary={t("label.meteo")} />
          </ListItemButton>

          <ListItemButton
            className="nested"
            component={NavLink}
            to={`/cases/${acase.id}/scenarios/${scenario.id}/lezs${firstLez ? `/${firstLez.id}` : ""}`}
          >
            <ListItemText primary={t("label.lowEmissionZones")} />
          </ListItemButton>

          <IsEnabled permission={permissions.scenario.residential}>
            <ListItemButton
              className="nested"
              component={NavLink}
              to={`/cases/${acase.id}/scenarios/${scenario.id}/residential-zones`}
            >
              <ListItemText primary={t("label.residentialZones")} />
            </ListItemButton>
          </IsEnabled>
        </List>
      </Collapse>
    );
  }
}

function CopyMenuItem(props: { acase: Case; scenario: Scenario; onClick: (event: MouseEvent) => void }) {
  const { acase, scenario } = props;
  const { t } = useTranslation();

  if (scenario.copyStatus === CopyStatus.FAILED) return null;
  if (scenario.copyStatus === CopyStatus.COPYING) return null;

  return (
    <MenuItem
      disabled={isDisabled(acase.permissions.copyScenario)}
      onMouseDown={(event) => event.stopPropagation()}
      onClick={props.onClick}
    >
      {t<string>("label.copyScenario")}
    </MenuItem>
  );
}

function DeleteMenuItem(props: { scenario: Scenario; onClick: (event: MouseEvent) => void }) {
  const { scenario } = props;
  const { t } = useTranslation();

  if (scenario.copyStatus === CopyStatus.COPYING) return null;

  return (
    <MenuItem
      onMouseDown={(event) => event.stopPropagation()}
      onClick={props.onClick}
      disabled={isDisabled(scenario.permissions.delete)}
    >
      {t("label.deleteScenario")}
    </MenuItem>
  );
}

function Status(props: { scenario: Scenario; open: boolean; onShowStatus: () => void }) {
  const { scenario, open } = props;
  const { t } = useTranslation();

  return render();

  function render() {
    // render based on status
    if (scenario.copyStatus === CopyStatus.COPYING) return renderCopying();
    if (scenario.copyStatus === CopyStatus.FAILED) return renderFailed();

    // if no specific status, the scenario can just be opened
    return (
      <ListItemIcon className="status-icon">
        {open && <ExpandLessIcon />}
        {!open && <ExpandMoreIcon />}
      </ListItemIcon>
    );
  }

  function renderCopying() {
    return (
      <ListItemIcon className="status-icon status-icon-button">
        <Tooltip
          title={[t<string>("label.scenarioIsBeingCopied"), t<string>("label.thisCouldTakeLongerThenExpected")].join(
            " "
          )}
          placement="right"
        >
          <IconButton disableFocusRipple disableTouchRipple size="large">
            <CachedIcon className="rotating" fontSize="small" />
          </IconButton>
        </Tooltip>
      </ListItemIcon>
    );
  }

  function renderFailed() {
    return (
      <>
        <ListItemIcon className="status-icon">
          <IconButton
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.stopPropagation();
              event.preventDefault();
              props.onShowStatus();
            }}
            size="large"
          >
            <ErrorIcon fontSize="small" />
          </IconButton>
        </ListItemIcon>
      </>
    );
  }
}

const Styles = styled("div")`
  .nested {
    padding-left: ${({ theme }) => theme.spacing(10)};
  }

  .scenario-list-header {
    padding-left: ${({ theme }) => theme.spacing(4)};
    min-height: 60px;

    .scenario-name {
      span {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }

  .scenario-list-items {
    padding: 0;
  }

  .status-icon {
    min-width: 30px;
  }

  .status-icon-button {
    min-width: 30px;
    transform: translateX(${({ theme }) => theme.spacing(-1)});
  }

  .rotating {
    animation: rotation 3s infinite cubic-bezier(0.23, 1, 0.32, 1);
  }

  @keyframes rotation {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(359deg);
    }
  }
`;
